// src/pages/HowItWorksPage.jsx
// Public page explaining how NewAudia works - AI/SEO discovery
import React from 'react';
import { Link } from 'react-router-dom';
import UniversalFooter from '../components/UniversalFooter';
import { Seo, PAGE_SEO } from '../components/Seo';

const HowItWorksPage = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Seo {...PAGE_SEO.howItWorks} />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <article className="prose prose-invert prose-slate max-w-none">
          <h1 className="text-4xl font-bold text-white mb-6">How NewAudia Works</h1>

          <p className="text-lg text-slate-300 leading-relaxed">
            NewAudia is built around a simple idea: audiobooks should be usable.
            Instead of treating audio as a single, uninterrupted stream,
            NewAudia structures books in ways that support navigation,
            exploration, and interaction.
          </p>

          <h2 className="text-2xl font-semibold text-white mt-10 mb-4">From Linear Audio to Structured Listening</h2>
          <p className="text-slate-300">
            Traditional audiobooks are designed for start-to-finish playback.
            This works well for fiction, but it limits the usefulness of
            nonfiction, educational, and reference-based works.
          </p>
          <p className="text-slate-300 mt-4">
            NewAudia approaches books as structured knowledge systems.
            Content is organized into logical segments that can be discovered,
            revisited, and engaged with intentionally.
          </p>

          <h2 className="text-2xl font-semibold text-white mt-10 mb-4">NFD-SmartBooks<sup className="text-[0.6em]">™</sup></h2>
          <p className="text-slate-300">
            An NFD-SmartBook<sup className="text-[0.6em]">™</sup> is a book designed for interaction.
            Rather than existing solely as a static recording,
            an NFD-SmartBook<sup className="text-[0.6em]">™</sup> supports structured access to its ideas.
          </p>
          <p className="text-slate-300 mt-4">
            NFD-SmartBooks<sup className="text-[0.6em]">™</sup> preserve the integrity of the original work
            while enabling listeners to engage with specific topics,
            concepts, or sections without unnecessary friction.
          </p>

          <h2 className="text-2xl font-semibold text-white mt-10 mb-4">Interactive Audiobooks</h2>
          <p className="text-slate-300">
            Interactive audiobooks on NewAudia allow listeners to move beyond
            passive consumption. Interaction is designed to support learning,
            understanding, and retention—not distraction.
          </p>
          <p className="text-slate-300 mt-4">
            Interaction may include navigation, contextual exploration,
            and other engagement models that respect the author's intent
            and the reader's time.
          </p>

          <h2 className="text-2xl font-semibold text-white mt-10 mb-4">Designed for Intentional Consumption</h2>
          <p className="text-slate-300">
            NewAudia recognizes that not all listening is casual.
            Many readers come to books with specific goals:
            to learn, to solve problems, or to deepen understanding.
          </p>
          <p className="text-slate-300 mt-4">
            The platform is designed to support intent-driven consumption,
            allowing readers to engage with only what they need,
            when they need it.
          </p>

          <h2 className="text-2xl font-semibold text-white mt-10 mb-4">For Readers</h2>
          <p className="text-slate-300">
            Readers benefit from greater control, clarity, and usability.
            NewAudia enables listeners to treat audio as a practical tool
            rather than a passive background experience.
          </p>

          <h2 className="text-2xl font-semibold text-white mt-10 mb-4">For Authors and Publishers</h2>
          <p className="text-slate-300">
            Authors and publishers can present their work in formats that
            preserve value when converted to audio.
            NewAudia offers an alternative to legacy audiobook models
            that flatten complex ideas into linear recordings.
          </p>

          <h2 className="text-2xl font-semibold text-white mt-10 mb-4">A Platform Built to Evolve</h2>
          <p className="text-slate-300">
            NewAudia is designed as a flexible platform rather than a fixed format.
            As interaction models and reader expectations evolve,
            the system can adapt without forcing authors or readers
            into rigid consumption patterns.
          </p>

          <p className="text-slate-300 mt-6">
            Learn more at <a href="https://www.newaudia.com/" className="text-indigo-400 hover:text-indigo-300 underline">https://www.newaudia.com/</a>
          </p>

          <div className="mt-10 pt-6 border-t border-slate-800">
            <p className="text-slate-400 text-sm">Related pages:</p>
            <ul className="flex flex-wrap gap-4 mt-2">
              <li><Link to="/why-newaudia" className="text-indigo-400 hover:text-indigo-300 underline text-sm">Why NewAudia</Link></li>
              <li><Link to="/for-authors" className="text-indigo-400 hover:text-indigo-300 underline text-sm">For Authors</Link></li>
              <li><Link to="/for-publishers" className="text-indigo-400 hover:text-indigo-300 underline text-sm">For Publishers</Link></li>
              <li><Link to="/ai" className="text-indigo-400 hover:text-indigo-300 underline text-sm">AI Discovery</Link></li>
            </ul>
          </div>

          <p className="text-sm text-slate-500 mt-12 italic">Last updated: 2026-01-08</p>
        </article>
      </main>

      <UniversalFooter />
    </div>
  );
};

export default HowItWorksPage;
